import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { getStandardizedSignatures } from "../../utils/formHelpers";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { request } from "../../utils/api";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import EvaluationInput from "../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../EvaluationNew/components/InputDatePicker";
import { employmentAgreementSchema } from "./application.validation";
import CloseButton from "../../components/Application/CloseButton";
import ApplicationFormFooter from "./ApplicationFormFooter";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";
import { useApplicationFormLogic } from "../../hooks/useApplicationFormLogic";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getLocalSystemDate, parseIsoDateToMDYUS } from "../../utils/dateUtils";
import { buildPayloadWithIsoDates } from "../../utils/applicationFormPayload";

const ISO_DATE_FIELDS = [
  "effectiveDate",
  "applicantSignedAt",
  "employerSignedAt",
  "reviewedBySignedAt",
];

const DEFAULT_POSITION = "Caregiver / Home Care Aide";

const ACK_ITEMS = [
  { name: "ackReadAgreement", label: "I acknowledge that I have read this Agreement." },
  { name: "ackUnderstandResponsibilities", label: "I understand my responsibilities." },
  { name: "ackAtWill", label: "I understand that my employment is at-will." },
  {
    name: "ackComplyPolicies",
    label: "I agree to comply with Company policies and applicable laws.",
  },
  {
    name: "ackNoGuaranteePeriod",
    label:
      "I understand that this Agreement does not guarantee employment for any specific period.",
  },
];

const JOB_RESPONSIBILITIES = [
  "Personal care assistance",
  "Companion care",
  "Meal preparation",
  "Medication reminders (via the Company's Medication Reminder Log sheets provided to the Employee)",
  "Mobility assistance",
  "Light housekeeping",
  "Transportation when authorized",
  "Timekeeping and documentation of services",
  "Reporting changes in client condition immediately",
];

const COMPLIANCE_LAWS = [
  "California labor laws",
  "Home Care Services Consumer Protection Act",
  "OSHA safety requirements",
  "HIPAA privacy requirements, where applicable",
  "Abuse reporting laws",
  "Company policies and procedures",
];

const CONFIDENTIAL_INFO = [
  "Client medical information",
  "Personal information",
  "Family information",
  "Financial information",
  "Company business records",
  "Employee records",
  "Pricing information",
  "Care plans",
  "Scheduling information",
];

const CONFIDENTIALITY_DUTIES = [
  "Maintain strict confidentiality.",
  "Access confidential information only when necessary to perform assigned duties.",
  "Never disclose confidential information except as authorized by law or Company policy.",
  "Comply with all HIPAA requirements when applicable.",
  "Return all confidential information upon separation from employment.",
];

const COMPANY_PROPERTY = [
  "Identification badges",
  "Keys",
  "Electronic devices",
  "Documents",
  "Client records",
  "Training materials",
  "Uniforms (if provided)",
  "Scheduling information",
  "Company credit cards",
  "Electronic files",
];

const PROFESSIONAL_CONDUCT = [
  "Treat clients with dignity and respect.",
  "Maintain professional boundaries.",
  "Report abuse, neglect, or exploitation immediately.",
  "Maintain punctuality and reliability.",
  "Follow infection-control procedures.",
  "Maintain accurate documentation.",
  "Refrain from discrimination, harassment, retaliation, or workplace violence.",
  "Maintain appropriate professional appearance.",
];

const NON_SOLICITATION_RIGHTS = [
  "Accepting future employment elsewhere",
  "Engaging in lawful competition after employment ends",
  "Exercising any rights protected under California law",
];

const SAFETY_ITEMS = [
  "Use proper lifting techniques.",
  "Follow infection-control procedures.",
  "Report workplace injuries immediately.",
  "Report unsafe conditions.",
  "Participate in required safety training.",
];

const PAYROLL_SCHEDULE = [
  "1st–15th of each month: Pay date is the 20th of the same month.",
  "16th–end of each month: Pay date is the 5th of the following month.",
];

/** Bold dynamic company name inline. */
const Co = ({ name }) => (
  <strong className="font-semibold text-slate-900">{name || "the Company"}</strong>
);

Co.propTypes = { name: PropTypes.string };

const FormCard = ({ title, number, children, className = "" }) => (
  <section
    className={`overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}
  >
    {title ? (
      <header className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-5 py-3">
        {number != null ? (
          <span className="flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full bg-slate-800 px-1.5 text-xs font-bold text-white">
            {number}
          </span>
        ) : null}
        <h3 className="text-sm font-bold tracking-wide text-slate-900 uppercase">
          {title}
        </h3>
      </header>
    ) : null}
    <div className="space-y-3 px-5 py-4 text-sm leading-relaxed text-slate-800">
      {children}
    </div>
  </section>
);

FormCard.propTypes = {
  title: PropTypes.string,
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

const PointList = ({ items, ordered = true }) => {
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag
      className={`my-1 space-y-1.5 ${
        ordered ? "list-decimal pl-6" : "list-disc pl-6"
      }`}
    >
      {items.map((item) => (
        <li key={item} className="pl-1 leading-snug text-slate-800">
          {item}
        </li>
      ))}
    </Tag>
  );
};

PointList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  ordered: PropTypes.bool,
};

const Field = ({
  label,
  name,
  disabled,
  multiline = false,
  rows = 2,
  className = "",
  inputClassName = "",
  hint,
}) => (
  <div className={`flex min-w-0 flex-col gap-1.5 ${className}`}>
    <label className="text-xs font-semibold tracking-wide text-slate-600 uppercase">
      {label}
    </label>
    <EvaluationInput
      name={name}
      type={multiline ? "textarea" : "text"}
      rows={multiline ? rows : undefined}
      disabled={disabled}
      className={`w-full ${inputClassName}`}
    />
    {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  hint: PropTypes.string,
};

const SubPoint = ({ title, children }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3">
    <p className="mb-1.5 text-xs font-bold tracking-wide text-slate-700 uppercase">
      {title}
    </p>
    <div className="space-y-2 text-sm leading-relaxed text-slate-800">
      {children}
    </div>
  </div>
);

SubPoint.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * Employment Agreement — CG / CNA Application form.
 * Structured card layout with aligned fields and numbered policy points.
 */
const EmploymentAgreement = ({ positionName, onClose }) => {
  const [agreementId, setAgreementId] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("");
  const { companyName } = useCurrentUser();
  const company = companyName || "the Company";

  const methods = useForm({
    resolver: zodResolver(employmentAgreementSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      employeeName: "",
      employeeAddress: "",
      employeePhone: "",
      position: DEFAULT_POSITION,
      effectiveDate: parseIsoDateToMDYUS(getLocalSystemDate()),
      emergencyContactName: "",
      emergencyContactPhone: "",
      emergencyContactRelationship: "",
      hourlyRate: "",
      ackReadAgreement: false,
      ackUnderstandResponsibilities: false,
      ackAtWill: false,
      ackComplyPolicies: false,
      ackNoGuaranteePeriod: false,
      applicantSignature: "",
      applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
      applicantName: "",
      applicantStatementAccepted: false,
      employerAuthorizedRepName: "",
      employerAuthorizedRepTitle: "",
      employerSignature: "",
      employerSignedAt: "",
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: "",
      comments: "",
    },
    mode: "onChange",
  });

  const { handleSubmit, setValue, watch, reset } = methods;

  const onSubmit = async (data, flag = "send_to_office", additionalData = {}) => {
    const actualFlag = typeof flag === "string" ? flag : "send_to_office";
    try {
      if (actualFlag === "send_to_office") {
        const missingAck = ACK_ITEMS.find((a) => !data[a.name]);
        if (missingAck) {
          toast.error("Please check all Employee Acknowledgment items before sending.");
          return;
        }
      }

      const payload = buildPayloadWithIsoDates(
        data,
        additionalData,
        ISO_DATE_FIELDS,
        { flag: actualFlag },
      );

      const finalComments =
        additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) payload.comments = finalComments;

      if (!payload.position) payload.position = DEFAULT_POSITION;

      const method = agreementId ? "PUT" : "POST";
      const url = agreementId
        ? `/employment-agreements/${agreementId}`
        : "/employment-agreements";

      const response = await request(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (response.status === 1) {
        toast.success(
          response.message || "Employment Agreement processed successfully!",
        );
        closeForm();
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error submitting Employment Agreement:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

  const {
    handleStatusAction,
    handleSave,
    targetUserId,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    closeForm,
    actualFromId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName,
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "employeeName",
    applicantSignatureDateField: "applicantSignedAt",
    applicantSignatureField: "applicantSignature",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus,
  });

  useEffect(() => {
    if (positionName) {
      const current = methods.getValues("position");
      if (!current) setValue("position", DEFAULT_POSITION);
    }
  }, [positionName, setValue, methods]);

  useEffect(() => {
    const fetchAgreement = async () => {
      if (!currentUserData) return;
      if (isAdmin && !targetUserId) return;

      try {
        if (!actualFromId && !targetUserId && !agreementId) {
          if (currentUserData && !isAdmin) {
            methods.reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              employeeName:
                `${currentUserData.firstName || ""} ${currentUserData.lastName || ""}`.trim(),
              employeeAddress: "",
              employeePhone: currentUserData.phone || "",
              position: DEFAULT_POSITION,
              effectiveDate: parseIsoDateToMDYUS(getLocalSystemDate()),
              applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
              ackReadAgreement: false,
              ackUnderstandResponsibilities: false,
              ackAtWill: false,
              ackComplyPolicies: false,
              ackNoGuaranteePeriod: false,
              applicantStatementAccepted: false,
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(
              `/employment-agreements/${actualFromId}?userId=${targetUserId}`,
            );
          } else {
            response = await request(
              `/employment-agreements?userId=${targetUserId}`,
            );
          }
        } else {
          response = await request(
            `/employment-agreements/${actualFromId || targetUserId}`,
          );
        }

        let data = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) data = response.result[0];
          } else if (response.result?.agreement) {
            data = response.result.agreement;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            data = response.result.items.find(
              (item) => item.userId === Number.parseInt(targetUserId, 10),
            );
          } else if (response.result?.id) {
            data = response.result;
          }
        }

        if (data) {
          setAgreementId(data.id);
          setCurrentStatus(data.status || "");
          reset({
            ...data,
            employeeName: data.employeeName || data.applicantName || "",
            position: data.position || DEFAULT_POSITION,
            effectiveDate:
              parseIsoDateToMDYUS(data.effectiveDate) ||
              parseIsoDateToMDYUS(getLocalSystemDate()),
            ...getStandardizedSignatures(data, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(
              data.applicantSignedAt || getLocalSystemDate(),
            ),
            employerSignedAt: parseIsoDateToMDYUS(data.employerSignedAt),
            reviewedBySignedAt: parseIsoDateToMDYUS(data.reviewedBySignedAt),
            ackReadAgreement: Boolean(data.ackReadAgreement),
            ackUnderstandResponsibilities: Boolean(
              data.ackUnderstandResponsibilities,
            ),
            ackAtWill: Boolean(data.ackAtWill),
            ackComplyPolicies: Boolean(data.ackComplyPolicies),
            ackNoGuaranteePeriod: Boolean(data.ackNoGuaranteePeriod),
            applicantStatementAccepted: Boolean(data.applicantStatementAccepted),
          });
        }
      } catch (error) {
        console.error("Error fetching Employment Agreement:", error);
      }
    };
    fetchAgreement();
  }, [
    targetUserId,
    reset,
    currentUserData,
    isAdmin,
    methods,
    actualFromId,
    agreementId,
  ]);

  const disabled = isReadOnly;

  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="mx-auto my-8 max-w-5xl overflow-hidden rounded-2xl bg-white shadow-xl">
        <EvaluationFormHeader title="Employment Agreement" />
        <div className="p-5 sm:p-8">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <CorrectionRequestAlert
                status={currentStatus}
                comments={methods.watch("comments")}
              />
              <fieldset disabled={disabled} className="space-y-5">
                {/* Intro */}
                <FormCard>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <p className="max-w-2xl text-sm leading-relaxed text-slate-700">
                      This Employment Agreement (&quot;Agreement&quot;) is entered
                      into between <Co name={company} /> (&quot;Company&quot;) and the
                      Employee named below.
                    </p>
                    <div className="w-full shrink-0 sm:w-44">
                      <label className="mb-1.5 block text-xs font-semibold tracking-wide text-slate-600 uppercase">
                        Effective Date
                      </label>
                      <InputDatePicker
                        name="effectiveDate"
                        formatSeparator="/"
                        allowManualEntry
                        disabled={disabled}
                        value={watch("effectiveDate") || ""}
                        onChange={(e) => setValue("effectiveDate", e.target.value)}
                        className="w-full"
                      />
                    </div>
                  </div>
                </FormCard>

                {/* Employee info */}
                <FormCard title="Employee Information">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field
                      label="Employee Name"
                      name="employeeName"
                      disabled={disabled}
                    />
                    <Field label="Phone" name="employeePhone" disabled={disabled} />
                    <Field
                      label="Position"
                      name="position"
                      disabled={disabled}
                      className="md:col-span-2"
                    />
                    <Field
                      label="Address"
                      name="employeeAddress"
                      disabled={disabled}
                      multiline
                      rows={2}
                      className="md:col-span-2"
                    />
                  </div>
                </FormCard>

                {/* Emergency contact */}
                <FormCard title="Emergency Contact">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <Field
                      label="Contact Name"
                      name="emergencyContactName"
                      disabled={disabled}
                    />
                    <Field
                      label="Contact Phone"
                      name="emergencyContactPhone"
                      disabled={disabled}
                    />
                    <Field
                      label="Relationship"
                      name="emergencyContactRelationship"
                      disabled={disabled}
                    />
                  </div>
                </FormCard>

                <FormCard title="Compensation" number={1}>
                  <p>
                    The Employee shall be compensated at the following hourly rates
                    based on the type of assignment accepted and worked:
                  </p>
                  <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                    <span className="pb-2 text-sm font-semibold text-slate-800">
                      Hourly Shifts
                    </span>
                    <div className="w-36">
                      <label className="mb-1 block text-xs font-semibold tracking-wide text-slate-600 uppercase">
                        Rate ($)
                      </label>
                      <EvaluationInput name="hourlyRate" disabled={disabled} />
                    </div>
                    <span className="pb-2 text-sm font-semibold text-slate-800">
                      per hour
                    </span>
                  </div>
                  <PointList
                    items={[
                      "Compensation shall be paid in accordance with the Company's regular payroll schedule and applicable federal, state, and local wage and hour laws.",
                      "The applicable hourly rate shall be determined by the scheduled assignment and approved by the Company prior to the start of each shift.",
                      "The Company reserves the right to revise compensation rates based on factors including employee performance, experience, certifications, assignment complexity, geographic location, client-specific requirements, or changes in Company compensation policies. Changes will be communicated in writing before becoming effective.",
                      "Certain assignments (holidays, last-minute shifts, specialized care, or high-acuity clients) may be eligible for additional compensation at the sole discretion of the Company. Any differential pay will be communicated and approved prior to the assignment.",
                    ]}
                  />
                </FormCard>

                <FormCard title="Job Responsibilities" number={2}>
                  <p>
                    Employees agree to perform assigned caregiving duties
                    professionally, safely, and compassionately, including but not
                    limited to:
                  </p>
                  <PointList items={JOB_RESPONSIBILITIES} />
                  <p>
                    Employee agrees to follow all Company policies, client care plans,
                    state regulations, and supervisor instructions.
                  </p>
                </FormCard>

                <FormCard title="Compliance with Laws" number={3}>
                  <p>Employee agrees to comply with all applicable:</p>
                  <PointList items={COMPLIANCE_LAWS} />
                  <p>
                    Employee shall maintain all required licenses, certifications, and
                    registrations necessary for employment.
                  </p>
                </FormCard>

                <FormCard title="Confidentiality and HIPAA" number={4}>
                  <p>
                    Employee understands that during employment they may have access to
                    confidential information, including:
                  </p>
                  <PointList items={CONFIDENTIAL_INFO} />
                  <p className="font-semibold text-slate-900">Employee agrees to:</p>
                  <PointList items={CONFIDENTIALITY_DUTIES} />
                  <p>This obligation continues after employment ends.</p>
                </FormCard>

                <FormCard title="Company Property" number={5}>
                  <p>
                    Employee acknowledges that all Company property remains the
                    exclusive property of <Co name={company} />, including:
                  </p>
                  <PointList items={COMPANY_PROPERTY} />
                  <p>
                    Upon separation from employment, Employee agrees to immediately
                    return all Company property.
                  </p>
                </FormCard>

                <FormCard title="Professional Conduct" number={6}>
                  <p>Employee agrees to:</p>
                  <PointList items={PROFESSIONAL_CONDUCT} />
                </FormCard>

                <FormCard title="Non-Solicitation (California-Compliant)" number={7}>
                  <PointList
                    items={[
                      "Nothing in this Agreement is intended to prohibit lawful competition or interfere with any rights protected under California law.",
                      "During employment, Employee agrees not to use Company confidential information or trade secrets to divert Company clients or employees for personal benefit.",
                    ]}
                  />
                  <p className="font-semibold text-slate-900">
                    Nothing in this Agreement prohibits Employee from:
                  </p>
                  <PointList items={NON_SOLICITATION_RIGHTS} />
                </FormCard>

                <FormCard title="Electronic Communications" number={8}>
                  <p>
                    The Employee understands that the Company email, electronic
                    devices, software, and communication systems are Company property
                    and may be monitored to the extent permitted by law.
                  </p>
                </FormCard>

                <FormCard title="Scheduling & Shift Assignments" number={9}>
                  <div className="space-y-3">
                    <SubPoint title="Shift Assignments">
                      <p>
                        The Company will assign shifts based on client needs,
                        caregiver availability, qualifications, and scheduling
                        requirements. Employees are expected to communicate their
                        availability accurately and promptly notify the Company of any
                        changes to their availability.
                      </p>
                    </SubPoint>
                    <SubPoint title="Shift Length">
                      <p>
                        Scheduled shifts may range from a minimum of four (4) hours up
                        to twelve (12) hours per individual caregiver within a
                        24-hour period. The Company may provide clients with up to
                        twenty-four (24) hours of care; however, care exceeding twelve
                        (12) hours will generally require coverage by an additional
                        caregiver.
                      </p>
                    </SubPoint>
                    <SubPoint title="Acceptance of Assignments">
                      <p>
                        Employees are responsible for confirming their availability
                        before accepting a scheduled assignment. Once an assignment
                        has been accepted and confirmed, the Employee is expected to
                        report to the scheduled shift on time and complete the
                        assignment as scheduled unless otherwise directed by the
                        Company.
                      </p>
                    </SubPoint>
                    <SubPoint title="Schedule Changes">
                      <p>
                        Client needs and schedules may occasionally change. The
                        Company will make reasonable efforts to provide Employees with
                        advance notice of schedule changes. Employees must not
                        independently alter their scheduled start time, end time, or
                        leave a client assignment early without prior approval from
                        the Company, except in an emergency.
                      </p>
                    </SubPoint>
                    <SubPoint title="Call-Offs and Emergencies">
                      <p>
                        If an Employee is unable to report for a scheduled shift, the
                        Employee must notify the Company as soon as possible in
                        accordance with the Company&apos;s call-off procedures.
                        Employees should not arrange for another caregiver to cover a
                        shift without prior approval from <Co name={company} />.
                      </p>
                    </SubPoint>
                    <SubPoint title="Additional Hours">
                      <p>
                        Employees may occasionally be offered additional hours or
                        assignments based on client needs and caregiver availability.
                        Employees are not required to accept shifts that have not been
                        agreed upon; however, once an assignment has been accepted,
                        the Employee is expected to fulfill the scheduled shift unless
                        an emergency or other approved circumstance prevents them from
                        doing so.
                      </p>
                    </SubPoint>
                    <SubPoint title="No Guarantee of Hours">
                      <p>
                        Employment with the Company does not guarantee a minimum
                        number of work hours or client assignments. Work schedules may
                        vary based on client needs, service availability, Employee
                        availability, and business operations.
                      </p>
                    </SubPoint>
                    <SubPoint title="Compliance With Wage and Hour Laws">
                      <p>
                        All scheduling and work assignments will be administered in
                        accordance with applicable federal, state, and local wage and
                        hour laws. Employees must accurately report all time worked,
                        including any time worked outside their scheduled shift.
                      </p>
                    </SubPoint>
                  </div>
                </FormCard>

                <FormCard title="Safety" number={10}>
                  <p>Employees agree to:</p>
                  <PointList items={SAFETY_ITEMS} />
                </FormCard>

                <FormCard title="Time Log Requirements" number={11}>
                  <PointList
                    items={[
                      `Employees are required to accurately record all hours actually worked for each client assignment using ${company}'s designated Time Log Sheet (attached). Time Log Sheets will be provided by ${company}; employees may also print additional copies as needed.`,
                      "Employees must record the actual start and end time of each shift, applicable meal or off-duty periods, total hours worked, and the services provided. All entries must be complete and accurate.",
                      `Completed and signed Time Log Sheets must be submitted to ${company} on the 15th and the last day of each month (the 30th or 31st, as applicable) for payroll processing.`,
                      `Failure to timely submit a Time Log Sheet may delay payroll processing; however, employees are required to report all hours actually worked, and ${company} will comply with all applicable wage and hour requirements regarding payment for time worked.`,
                    ]}
                  />
                </FormCard>

                <FormCard title="Payroll" number={12}>
                  <p>
                    Employee&apos;s compensation will be calculated based upon verified
                    hours worked and applicable rates of pay. Employee is responsible
                    for submitting accurate time log sheets in accordance with the
                    Company&apos;s established payroll deadlines.
                  </p>
                  <p className="font-semibold text-slate-900">Payroll Schedule</p>
                  <PointList items={PAYROLL_SCHEDULE} />
                  <PointList
                    items={[
                      "Payment Method: Employees may receive wages through direct deposit or paper check. Employees must select their preferred payment method and provide any required information or authorization forms.",
                      `Employees who wish to change their selected payment method must notify ${company} at least five (5) calendar days before the next scheduled payroll date. Detailed payroll payment procedures are outlined in the Employee Handbook.`,
                    ]}
                  />
                </FormCard>

                <FormCard title="Training" number={13}>
                  <PointList
                    items={[
                      "Employee agrees to complete all required orientation, continuing education, and mandatory in-service training.",
                      "Completion of Company-provided training does not create a contractual obligation to remain employed for any minimum period.",
                    ]}
                  />
                </FormCard>

                <FormCard title="Arbitration" number={14}>
                  <PointList
                    items={[
                      "If separately agreed in writing, the Company and Employee may agree to submit covered employment-related disputes to binding arbitration in accordance with applicable California and federal law.",
                      "Nothing in this Agreement waives rights that cannot legally be waived or limits an employee's right to file charges or participate in proceedings before government agencies.",
                    ]}
                  />
                </FormCard>

                <FormCard title="Resignation and Care Transition" number={15}>
                  <div className="space-y-3">
                    <SubPoint title="Notice of Resignation">
                      <p>
                        Employees are requested to provide <Co name={company} /> with at
                        least seven (7) calendar days&apos; written notice before
                        voluntarily resigning from employment whenever reasonably
                        possible.
                      </p>
                    </SubPoint>
                    <SubPoint title="Caregiver Transition">
                      <p>
                        When an Employee provides notice of resignation,{" "}
                        <Co name={company} /> may require the Employee to participate in
                        a reasonable care transition and handoff with a replacement
                        caregiver. This may include providing information regarding the
                        client&apos;s routine, preferences, care needs, household
                        procedures, and other relevant information necessary to ensure
                        a smooth transition of care.
                      </p>
                    </SubPoint>
                    <p>
                      The Employee is expected to cooperate with reasonable transition
                      and handoff activities during their remaining scheduled work
                      time. All time spent performing required transition or handoff
                      duties must be accurately recorded and will be compensated in
                      accordance with applicable law.
                    </p>
                    <p>
                      Nothing in this provision limits an Employee&apos;s right to
                      resign or otherwise changes the at-will employment relationship.
                    </p>
                  </div>
                </FormCard>

                <FormCard title="Entire Agreement" number={16}>
                  <PointList
                    items={[
                      "This Agreement represents the entire understanding between the parties regarding the subjects addressed herein and supersedes any prior oral representations concerning those subjects.",
                      "If any provision is determined to be unenforceable, the remaining provisions shall remain in effect to the fullest extent permitted by law.",
                      "California law governs this Agreement.",
                    ]}
                  />
                </FormCard>

                <FormCard title="Employee Acknowledgment">
                  <p className="text-slate-600">
                    Please review and check each item below before signing and sending
                    this Agreement.
                  </p>
                  <div className="space-y-2">
                    {ACK_ITEMS.map((item, index) => (
                      <label
                        key={item.name}
                        className={`flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-800 transition-colors ${
                          disabled
                            ? "cursor-default opacity-80"
                            : "cursor-pointer hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-slate-200 text-[10px] font-bold text-slate-700">
                          {index + 1}
                        </span>
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          checked={Boolean(watch(item.name))}
                          disabled={disabled}
                          onChange={(e) => setValue(item.name, e.target.checked)}
                        />
                        <span className="leading-snug">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </FormCard>

                <ApplicationFormFooter
                  page={1}
                  totalPages={1}
                  methods={methods}
                  onSave={handleSave}
                  onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                  onStatusUpdate={handleStatusAction}
                  isSignatureModalOpen={isSignatureModalOpen}
                  openSignatureModal={openSignatureModal}
                  closeSignatureModal={closeSignatureModal}
                  handleSignatureApply={handleSignatureApply}
                  signerName={signerName}
                  applicantName={watch("employeeName") || ""}
                  applicantSignature={watch("applicantSignature")}
                  applicantSignedAt={watch("applicantSignedAt")}
                  reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                  reviewedBySignature={watch("reviewedBySignature")}
                  reviewedBySignedAt={watch("reviewedBySignedAt")}
                  submitLabel="Submit & Print"
                  isReadOnly={isReadOnly}
                  onClose={closeForm}
                  currentStatus={currentStatus}
                />
              </fieldset>
            </form>
          </FormProvider>
        </div>
      </div>
    </>
  );
};

EmploymentAgreement.propTypes = {
  positionName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default EmploymentAgreement;
